'use client';

import { useState, type ReactElement } from 'react';
import type { FooterBlockProps, NavLink } from '../types';
import {
  canonicalLegalSlug,
  logoForBackground,
  luminance,
  readableTextOn,
} from '@realsite/site-config';

/**
 * The `simple` and `simon-gray` footer designs.
 *
 * These were two components in apps/tenant-sites (components/Footer.tsx and sgr/Footer.tsx) that
 * read the site config directly, so the Studio could not draw them: it previewed every site with
 * the column footer while the live site showed one of these. They now take the same block props
 * as the column footer, plus the site data the host resolves (`props.site`), so both surfaces
 * render this one copy. The markup is the live components' markup, unchanged — a site that showed
 * one of these footers shows the same footer.
 */

const SOCIAL_ICONS: Record<string, ReactElement> = {
  facebook: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  ),
  instagram: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
    </svg>
  ),
  linkedin: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
    </svg>
  ),
  youtube: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  ),
};

/** The legal links every footer has always carried when the catalogue can't be read. */
const FALLBACK_LEGAL: NavLink[] = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Use', href: '/terms' },
];

/** An uploaded legal document (an absolute URL) opens in a new tab; a page on this site does not. */
const isExternal = (href: string) => /^https?:\/\//i.test(href);

const defaultCopyright = (props: FooterBlockProps) =>
  props.copyright?.trim() || `© ${new Date().getFullYear()} ${props.companyName}. All rights reserved.`;

/** The dark four-column footer (was apps/tenant-sites components/Footer.tsx). */
export function SimpleFooter(props: FooterBlockProps) {
  const [logoBroken, setLogoBroken] = useState(false);
  const year = new Date().getFullYear();
  const site = props.site ?? {};
  const siteName = props.companyName;
  // The footer sits on a dark background (gray-900): pick the reversed/light logo, only whitening a
  // dark logo if that's all there is. onError → the brand name, so a 404'd asset never shows broken.
  // A colour set on the footer section (the chat's "make the footer navy") paints this design too.
  const pinned = props.backgroundColor?.trim() || null;
  const footerLogo = logoForBackground(pinned ?? '#111827', { primary: site.logoUrl, reversed: site.logoDarkUrl });
  const { phone, email, address } = props;
  const social = site.social ?? {};
  const quickLinksHeading = props.quickLinksHeading?.trim() || 'Quick Links';
  const contactHeading = props.contactHeading?.trim() || 'Contact Us';
  const quickLinks = props.navLinks?.length ? props.navLinks : site.quickLinks ?? [];
  // A quick link to /privacy already reaches the privacy page; don't add /privacy-policy beside it.
  const linked = new Set(quickLinks.map((l) => canonicalLegalSlug(l.href)));
  const legalLinks = (props.legalPages?.length ? props.legalPages : FALLBACK_LEGAL)
    .filter((l) => !linked.has(canonicalLegalSlug(l.href)));
  const copyright = defaultCopyright(props);
  const showLogo = props.showLogo !== false;
  // The gray-100 / gray-400 text belongs to the default gray-900 footer. On a colour set on the
  // footer section the text takes that colour's readable text (set on <footer>): full strength for
  // the name and headings, softened for the rest. Hard-coded light grays made the headings white
  // on white on a white footer (Homes in Paradise SWFL, 2026-10-20).
  const tone = pinned
    ? { strong: '', soft: 'opacity-75 hover:opacity-100 transition-opacity', text: 'opacity-75', faint: 'opacity-60', link: 'hover:opacity-100 transition-opacity' }
    : { strong: 'text-white', soft: 'text-gray-400 hover:text-white transition-colors', text: 'text-gray-400', faint: 'text-gray-500', link: 'hover:text-gray-300 transition-colors' };
  const headingTone = pinned ? '' : 'text-gray-100';

  const socialLinks = [
    { platform: 'facebook', url: social.facebook },
    { platform: 'instagram', url: social.instagram },
    { platform: 'linkedin', url: social.linkedin },
    { platform: 'youtube', url: social.youtube },
  ].filter((s): s is { platform: string; url: string } => Boolean(s.url));

  return (
    <footer
      className={pinned ? undefined : 'bg-gray-900 text-gray-300'}
      style={pinned ? { backgroundColor: pinned, color: props.textColor?.trim() || readableTextOn(pinned) } : undefined}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main footer grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand column */}
          <div className="lg:col-span-1">
            {showLogo && footerLogo && !logoBroken ? (
              <img
                src={footerLogo.url}
                alt={siteName}
                width={160}
                height={40}
                onError={() => setLogoBroken(true)}
                className={`h-10 max-w-[160px] object-contain mb-3 ${footerLogo.filterClass}`}
              />
            ) : (
              <h3 className={`text-xl font-heading font-bold mb-3 ${tone.strong}`}>{siteName}</h3>
            )}
            {phone && (
              <a
                href={`tel:${phone.replace(/\D/g, '')}`}
                className={`block text-sm ${tone.soft} mb-1`}
              >
                {phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className={`block text-sm ${tone.soft}`}>
                {email}
              </a>
            )}

            {/* Social links */}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 mt-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={tone.soft}
                    aria-label={`Follow on ${link.platform}`}
                  >
                    {SOCIAL_ICONS[link.platform] || <span className="text-sm">{link.platform}</span>}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`text-sm font-semibold uppercase tracking-wider ${headingTone} mb-3`}>{quickLinksHeading}</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className={`text-sm ${tone.soft}`}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className={`text-sm font-semibold uppercase tracking-wider ${headingTone} mb-3`}>{contactHeading}</h4>
            <ul className="space-y-2">
              {address ? <li className={`text-sm ${tone.text}`}>{address}</li> : null}
              {phone ? (
                <li>
                  <a
                    href={`tel:${phone.replace(/\D/g, '')}`}
                    className={`text-sm ${tone.soft}`}
                  >
                    {phone}
                  </a>
                </li>
              ) : null}
              {email ? (
                <li>
                  <a href={`mailto:${email}`} className={`text-sm ${tone.soft}`}>
                    {email}
                  </a>
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className={`text-sm ${tone.faint}`}>{copyright}</p>
          <div className={`flex gap-4 text-sm ${tone.faint}`}>
            {/* From the platform catalogue, so a state document an agent enables appears here
                without a deploy. Falls back to the two every site has always linked. */}
            {legalLinks.map((link) =>
              isExternal(link.href) ? (
                <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer"
                   className={tone.link}>
                  {link.label}
                </a>
              ) : (
                <a key={link.href} href={link.href} className={tone.link}>
                  {link.label}
                </a>
              ),
            )}
            <a href="/sitemap.xml" className={tone.link}>
              Sitemap
            </a>
          </div>
        </div>

        {/* Fair Housing / REALTOR disclaimer */}
        <p className={`mt-6 text-xs ${pinned ? tone.faint : 'text-gray-600'} text-center leading-relaxed`}>
          Information deemed reliable but not guaranteed. Properties are subject to prior sale or
          withdrawal. © {year} {siteName}. Equal Housing Opportunity.
        </p>
      </div>
    </footer>
  );
}

/** The Simon Gray template's footer (was apps/tenant-sites components/sgr/Footer.tsx). */
export function SimonGrayFooter(props: FooterBlockProps) {
  const site = props.site ?? {};
  const logoUrl = site.logoUrl;
  const siteName = props.companyName;
  const { tagline, email, phone, address } = props;
  // A colour set on the footer section wins over the brand's primary, with its own text colour.
  const pinned = props.backgroundColor?.trim() || null;
  const primaryColor = pinned || site.primaryColor || undefined;
  // The tenant's `text_on_dark` where this resolves to "light text" — the same control that
  // paints every other dark surface on their site, rather than a white chosen here.
  const onPrimary = (pinned && props.textColor?.trim()) || readableTextOn(primaryColor, site.textOnDark);
  // Is that a LIGHT text colour? Decided by luminance, not by string equality with '#FFFFFF':
  // the logo filter inverts for light text and blackens for dark.
  const onPrimaryIsLight = (luminance(onPrimary) ?? 1) > 0.5;
  const quickLinksHeading = props.quickLinksHeading?.trim() || 'Quick Links';
  const contactHeading = props.contactHeading?.trim() || 'Contact Us';
  const copyright = defaultCopyright(props);
  const showLogo = props.showLogo !== false;

  // Footer-designated nav items (location:'footer') drive the footer links when any are set;
  // otherwise fall back to mirroring the top-level header nav (SGR's long-standing behavior).
  const footerLinks = props.navLinks?.length ? props.navLinks : site.headerLinks ?? [];

  // The site's legal pages come from the platform catalogue; an agent's OWN page wins when they
  // have one, so their title and casing are preserved and nothing appears twice.
  const mergedFooterLinks: NavLink[] = [...footerLinks, ...(site.authoredLegalLinks ?? [])];
  const seen = new Set(mergedFooterLinks.map((l) => canonicalLegalSlug(l.href)));
  for (const link of props.legalPages?.length ? props.legalPages : FALLBACK_LEGAL) {
    const key = canonicalLegalSlug(link.href);
    if (seen.has(key)) continue;
    seen.add(key);
    mergedFooterLinks.push(link);
  }

  const socialLinks = Object.entries(site.social ?? {})
    .filter((entry): entry is [string, string] => !!entry[1])
    .map(([platform, url]) => ({ platform, url }));

  return (
    <footer style={{ backgroundColor: primaryColor, color: onPrimary }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            {showLogo && logoUrl ? (
              <img
                src={logoUrl}
                alt={siteName}
                width={160}
                height={40}
                className={`h-10 max-w-[160px] object-contain mb-4 ${onPrimaryIsLight ? 'brightness-0 invert' : 'brightness-0'}`}
              />
            ) : (
              <div className="text-xl font-bold tracking-wide mb-4">{siteName}</div>
            )}
            {tagline && <p className="text-sm opacity-80 mb-4">{tagline}</p>}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 mt-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opacity-70 hover:opacity-100 transition-opacity"
                    aria-label={`Follow on ${link.platform}`}
                  >
                    {SOCIAL_ICONS[link.platform] || <span className="text-sm capitalize">{link.platform}</span>}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          {mergedFooterLinks.length > 0 && (
            <div>
              <h4 className="text-lg font-bold mb-4">{quickLinksHeading}</h4>
              <ul className="space-y-2 text-sm opacity-80">
                {mergedFooterLinks.map((link) => (
                  <li key={link.href}>
                    {link.href.startsWith('http') ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity">
                        {link.label}
                      </a>
                    ) : (
                      <a href={link.href} className="hover:opacity-100 transition-opacity">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-4">{contactHeading}</h4>
            <ul className="space-y-2 text-sm opacity-80">
              {address && <li>{address}</li>}
              {email && (
                <li>
                  <a href={`mailto:${email}`} className="hover:opacity-100 transition-opacity">
                    {email}
                  </a>
                </li>
              )}
              {phone && (
                <li>
                  <a href={`tel:${phone.replace(/\D/g, '')}`} className="hover:opacity-100 transition-opacity">
                    {phone}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t mt-8 pt-8 text-center text-sm opacity-60" style={{ borderColor: `${onPrimary}33` }}>
          {copyright}
        </div>
      </div>
    </footer>
  );
}
